// Terms of Service page — public, routed at /terms, linked from the footer.
//
// 🔴🔴 MUST DO BEFORE LAUNCH — do NOT open public signups until BOTH are done:
//   1. §15 Governing Law — replace the placeholder with the REAL legal entity
//      name + governing-law jurisdiction.
//   2. Counsel review — have a lawyer (or a Termly / Iubenda jurisdiction pass)
//      review these Terms, especially §6 (AI output disclaimer, no-copyright
//      warranty, infringement = user's liability + indemnity) and §11
//      (liability cap). Language is standard + solid, but this is not legal
//      advice and enforceability varies by jurisdiction.
//
// Also confirm before launch: contact email support@adastronaut.com actually
// receives mail, and the refund wording (§4) matches your Stripe refund policy.

import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const LAST_UPDATED = 'July 23, 2026';
const CONTACT_EMAIL = 'support@adastronaut.com';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display text-xl md:text-2xl font-bold text-dark mb-3">{title}</h2>
      <div className="space-y-3 text-dark-muted text-sm md:text-base leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
          <div className="mb-10">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-dark mb-2">
              Terms of Service
            </h1>
            <p className="text-sm text-dark-muted">Last updated: {LAST_UPDATED}</p>
          </div>

          <div className="space-y-10">
            <Section id="acceptance" title="1. Acceptance of Terms">
              <p>
                These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of
                Ad Astronaut (the &ldquo;Service&rdquo;), operated by Ad Astronaut
                (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;). By creating an account or
                using the Service, you agree to these Terms and to our{' '}
                <a href="/privacy" className="text-primary font-semibold hover:underline">Privacy Policy</a>.
                If you do not agree, do not use the Service.
              </p>
            </Section>

            <Section id="eligibility" title="2. Eligibility &amp; Accounts">
              <ul className="list-disc pl-5 space-y-2">
                <li>You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Service.</li>
                <li>You are responsible for the accuracy of your account information and for keeping your login credentials secure.</li>
                <li>You are responsible for all activity that occurs under your account.</li>
                <li>If you use the Service on behalf of an organization, you represent that you are authorized to bind that organization to these Terms.</li>
              </ul>
            </Section>

            <Section id="service" title="3. The Service">
              <p>
                The Service lets you generate marketing images and social media content from your
                products using artificial intelligence, edit and store those creatives, and optionally
                schedule or publish them to social platforms you connect. We may add, change, or remove
                features at any time.
              </p>
            </Section>

            <Section id="credits" title="4. Credits, Plans &amp; Payments">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Generating content consumes <span className="font-semibold text-dark">credits</span>.
                  Credits are provided with a free trial, monthly subscription plans, and one-time
                  top-up purchases.
                </li>
                <li>
                  Paid plans renew automatically each billing period until cancelled. You can cancel at
                  any time; cancellation stops future renewals but does not retroactively refund the
                  current period.
                </li>
                <li>
                  Payments are processed by Stripe. By purchasing, you authorize us and Stripe to charge
                  your payment method for the amounts shown at checkout, plus any applicable taxes.
                </li>
                <li>
                  Credits have no cash value, are non-transferable, and — except as required by law or
                  our stated refund policy — are non-refundable. Unused subscription credits may expire
                  or reset at the end of each billing period as described at checkout.
                </li>
                <li>
                  If a generation fails due to an error on our side, the credits reserved for it are
                  returned to your balance.
                </li>
              </ul>
            </Section>

            <Section id="your-content" title="5. Your Content">
              <p>
                &ldquo;Your Content&rdquo; means the images, text, brand assets, URLs, and other
                materials you upload or provide. As between you and us, you retain such rights as you
                may have in Your Content and in the creatives you generate; however, we make no
                representation that any such rights exist or are enforceable — see Section 6.
              </p>
              <p>
                You represent and warrant that you own or have all rights, licenses, and permissions
                necessary to upload and use Your Content, including any product images, logos,
                trademarks, and depictions of people, and that Your Content does not infringe any
                third party&rsquo;s intellectual property, privacy, or other rights.
              </p>
              <p>
                You grant us a limited, non-exclusive license to host, process, and transmit Your
                Content solely to provide and operate the Service (including sending it to the AI and
                infrastructure providers described in our Privacy Policy, and publishing it to social
                accounts you connect). We do not use Your Content to train our own models.
              </p>
            </Section>

            <Section id="ai" title="6. AI-Generated Content &amp; Ownership">
              <p>
                The Service uses third-party AI models to generate content. AI output can be
                unpredictable, may contain inaccuracies, and is not guaranteed to be unique, accurate,
                or free of similarity to other outputs. We do not warrant that generated content will
                meet your expectations, be fit for any particular purpose, or be suitable for
                commercial use. You are solely responsible for reviewing generated content before you
                use or publish it, and for ensuring it is truthful, does not infringe others&rsquo;
                rights, and complies with all applicable laws, advertising standards, and the policies
                of any platform you publish to.
              </p>
              <p>
                <span className="font-semibold text-dark">No ownership or copyright warranty.</span>{' '}
                We do not claim ownership of the creatives you generate. At the same time, we make no
                representation or warranty that any generated content is original, is protectable by
                copyright or other intellectual-property rights, or is free of similarity to — or
                infringement of — the rights of any third party. AI-generated material may not be
                eligible for copyright protection in some jurisdictions, and we do not grant, assign,
                transfer, or guarantee any copyright or other ownership right in it.
              </p>
              <p>
                <span className="font-semibold text-dark">Your responsibility.</span>{' '}
                You are solely responsible for determining whether you may lawfully use, reproduce,
                distribute, or publish any generated content, and for obtaining any rights, licenses,
                or clearances required to do so — including for any trademarks, logos, likenesses, or
                copyrighted material reflected in your inputs or in the output. You assume all risk
                arising from your use of generated content.
              </p>
              <p>
                <span className="font-semibold text-dark">No liability for infringement.</span>{' '}
                To the maximum extent permitted by law, we are not liable for any claim that generated
                content — or your use or publication of it — infringes, misappropriates, or otherwise
                violates any third party&rsquo;s intellectual-property, publicity, privacy, or other
                rights. Any such liability rests solely with you, and you agree to indemnify and hold
                us harmless from those claims as described in Section 12.
              </p>
            </Section>

            <Section id="acceptable-use" title="7. Acceptable Use">
              <p>You agree not to use the Service to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Upload content you do not have the rights to, or that infringes others&rsquo; rights.</li>
                <li>Create content that is unlawful, deceptive, defamatory, hateful, harassing, sexually explicit, or that depicts real individuals without consent.</li>
                <li>Generate false or misleading advertising, impersonate any person or brand, or violate any platform&rsquo;s policies.</li>
                <li>Attempt to access another user&rsquo;s account or data, probe or circumvent security, or overload or disrupt the Service.</li>
                <li>Scrape, reverse-engineer, resell, or use the Service to build a competing product, except as permitted by law.</li>
                <li>Use the Service in violation of the usage policies of our AI providers or of any applicable law.</li>
              </ul>
              <p>We may remove content or suspend accounts that violate these Terms.</p>
            </Section>

            <Section id="third-party" title="8. Connected Platforms &amp; Third-Party Services">
              <p>
                When you connect a social account (such as Facebook or Instagram) and choose to
                publish, you authorize us to post on your behalf. Your use of those platforms is
                governed by their own terms and policies, and we are not responsible for their
                availability, changes, or actions. The Service also relies on third-party providers
                described in our Privacy Policy.
              </p>
            </Section>

            <Section id="ip" title="9. Our Intellectual Property">
              <p>
                The Service itself — including its software, templates, design, and branding
                (excluding Your Content) — is owned by us or our licensors and is protected by
                intellectual-property laws. We grant you a limited, non-exclusive, non-transferable
                right to use the Service in accordance with these Terms.
              </p>
            </Section>

            <Section id="disclaimer" title="10. Disclaimers">
              <p>
                The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without
                warranties of any kind, whether express or implied, including warranties of
                merchantability, fitness for a particular purpose, and non-infringement. We do not
                warrant that the Service will be uninterrupted, error-free, or that generated content
                will meet your expectations.
              </p>
            </Section>

            <Section id="liability" title="11. Limitation of Liability">
              <p>
                To the maximum extent permitted by law, we will not be liable for any indirect,
                incidental, special, consequential, or punitive damages, or for lost profits, data, or
                goodwill, arising out of or related to your use of the Service. Our total liability for
                any claim relating to the Service will not exceed the greater of the amount you paid us
                in the twelve months before the claim, or one hundred U.S. dollars (USD $100).
              </p>
            </Section>

            <Section id="indemnity" title="12. Indemnification">
              <p>
                You agree to indemnify and hold us harmless from any claims, damages, liabilities, and
                expenses (including reasonable legal fees) arising from Your Content, your use of the
                Service, or your violation of these Terms or of any law or third-party right.
              </p>
            </Section>

            <Section id="termination" title="13. Termination">
              <p>
                You may stop using the Service and delete your account at any time. We may suspend or
                terminate your access if you violate these Terms or if we discontinue the Service. Upon
                termination, your right to use the Service ends; provisions that by their nature should
                survive (such as content warranties, disclaimers, limitation of liability, and
                indemnification) will survive.
              </p>
            </Section>

            <Section id="changes" title="14. Changes to These Terms">
              <p>
                We may update these Terms from time to time. When we do, we will revise the
                &ldquo;Last updated&rdquo; date above and, where appropriate, provide additional
                notice. Your continued use of the Service after changes take effect constitutes
                acceptance of the updated Terms.
              </p>
            </Section>

            <Section id="governing-law" title="15. Governing Law">
              <p>
                These Terms are governed by the laws of the jurisdiction in which we operate, without
                regard to conflict-of-law principles. Any disputes will be resolved in the courts of
                that jurisdiction, unless otherwise required by applicable law.
              </p>
            </Section>

            <Section id="contact" title="16. Contact Us">
              <p>
                Questions about these Terms? Contact us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary font-semibold hover:underline">
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
            </Section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
